import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Exclusive add-on behaviour: the offer card in the cart drawer, and the
 * pre-checkout prompt.
 *
 * Both entry points share one add routine, so there is a single definition of
 * "add the towel" and a single guard against double submission.
 */

/** Serialises adds so a double click cannot put two towels in the cart. */
let busy = false;

/** Set while re-submitting checkout, so the interceptor lets that click through. */
let proceeding = false;

/** The section ids the theme's own cart components re-render from. */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

/**
 * Adds one add-on to the cart and hands the returned section HTML to the theme
 * through the same event product-form-component dispatches, so the drawer, the
 * badge, the totals and the cart icon all update through Horizon's existing path.
 *
 * @param {string} variantId
 * @returns {Promise<boolean>} whether the cart actually changed
 */
export async function addAddon(variantId) {
  if (busy || !variantId) return false;
  busy = true;

  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart-addon',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: cartSectionIds().join(','),
      }),
    });

    const added = await response.json();
    if (added.status) throw new Error(added.description || added.message || 'Add to cart failed');

    // /cart/add.js returns only the added lines, so read the whole cart back the
    // way product-form does before announcing the update.
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-addon',
        sourceId: 'cart-addon',
        sections: added.sections,
        didError: false,
      },
    });

    return true;
  } catch (error) {
    console.error('[cart-addon]', error);
    deferred.reject(error);
    return false;
  } finally {
    busy = false;
  }
}

/**
 * Removes a line by key. Used when the last eligible product leaves the cart and
 * the add-on is stranded.
 *
 * @param {string} lineKey
 */
async function removeLine(lineKey) {
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart-addon',
      lines: [{ merchandiseId: lineKey, quantity: 0 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections: cartSectionIds().join(',') }),
    });

    const cart = await response.json();
    if (cart.status) throw new Error(cart.message || 'Cart update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-addon',
        sourceId: 'cart-addon-cleanup',
        sections: cart.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[cart-addon]', error);
    deferred.reject(error);
  }
}

/** The offer card, and the stranded-add-on cleanup. */
class CartAddonComponent extends HTMLElement {
  static cleaning = null;

  connectedCallback() {
    const orphanKey = this.dataset.orphanKey;

    if (orphanKey) {
      if (CartAddonComponent.cleaning === orphanKey) return;
      CartAddonComponent.cleaning = orphanKey;
      removeLine(orphanKey);
      return;
    }

    const button = this.querySelector('.cart-addon__button');
    if (!button) return;

    button.addEventListener('click', async () => {
      button.setAttribute('aria-busy', 'true');
      const ok = await addAddon(button.dataset.variantId);
      // On success the drawer re-renders without this card, so only the failure
      // path needs the button putting back.
      if (!ok) button.removeAttribute('aria-busy');
    });
  }
}

if (!customElements.get('cart-addon-component')) {
  customElements.define('cart-addon-component', CartAddonComponent);
}

/**
 * Pre-checkout prompt.
 *
 * Whether to offer at all is decided in Liquid: the dialog is only rendered when
 * the cart holds an eligible product and does not already hold the add-on. If it
 * is absent, checkout is never intercepted.
 */
function findCheckoutButton() {
  return document.querySelector('button[name="checkout"]');
}

function proceedToCheckout() {
  proceeding = true;
  const button = findCheckoutButton();

  if (button && button.form) {
    button.form.requestSubmit(button);
  } else if (button) {
    button.click();
  }
}

document.addEventListener(
  'click',
  (event) => {
    if (proceeding) return;

    const target = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
    if (!target) return;

    // Accelerated wallets (Shop Pay, Apple Pay) are separate buttons and are not
    // matched here, so they check out untouched.
    const dialog = document.querySelector('.cart-addon-upsell');
    if (!dialog || dialog.open) return;

    event.preventDefault();
    event.stopPropagation();

    dialog.showModal();
  },
  true
);

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const dialog = target.closest('.cart-addon-upsell');
  if (!dialog) return;

  if (target.closest('[data-addon-decline]')) {
    dialog.close();
    proceedToCheckout();
    return;
  }

  const accept = target.closest('[data-addon-accept]');
  if (!accept) return;

  accept.setAttribute('aria-busy', 'true');

  // Wait for Shopify to confirm the line before leaving for checkout, so the
  // order cannot depart without it.
  const ok = await addAddon(accept.dataset.variantId);

  accept.removeAttribute('aria-busy');
  dialog.close();

  if (ok) {
    // The cart re-renders after the add, so give the morph a turn before
    // submitting the refreshed form.
    setTimeout(proceedToCheckout, 0);
  } else {
    proceedToCheckout();
  }
});
